// The `tile` structure symbol set: card 14 of mockup round 054, "HEX TILE
// BADGE" (R1103, R1104, `ui-map-interface.md` §6.2).
//
// The mark is a board-game tile: a hex of r 26 that fills most of the own
// cell, in the holder's ink, with a bold icon per kind cut out of it in the
// water's dark. The Trench Throne takes a tile of r 29, because it is the
// capital. A neutral tile is grey and thin, which the card states as a lower
// fill alpha.
//
// The contested tell: the tile splits on the diagonal and the lower-right
// half takes the attacker's ink, under a white seam. The card clips the hex
// against a half plane; there is no clip here, so the half is stated as its
// own polygon, cut where the hex outline crosses the line `x + y = 0`.
//
// The card lets the seam flicker. The atlas bakes the still mark (R1104), so
// the seam stands at the middle of the alpha the card gives it.
//
// Every offset, weight, radius and alpha below is the figure card 14 draws.

import { hexPoints, type MarkGraphics } from '../marks';
import {
  cubic, disc, ellipseArc, line, quadratic, stroke,
  WATER, setOf, type StructureDraw, type StructureInk, type StructureKind,
  type StructureSet,
} from './shared';

/** The tile of one kind, and the wider tile of the capital. */
const TILE_R = 26;
const THRONE_TILE_R = 29;

/** The seam ink. The card draws the split line in white. */
const WHITE = 0xffffff;

/**
 * The still alpha of the seam.
 *
 * The card flickers it between 0.95 and 0.35 with the clock; the atlas bakes
 * one cell, so the seam stands at the middle of the two (R1104).
 */
const SEAM_ALPHA = 0.65;

/**
 * The lower-right half of the tile, cut on the diagonal `x + y = 0`.
 *
 * Three of the six vertices stand on that side. The outline crosses the two
 * edges that join them to the other three, and it crosses each at
 * `r(3 - sqrt 3) / 2` from the origin on both axes. The list runs along the
 * hex outline, so a `poly` with `close` false strokes the outline alone and a
 * closed `poly` fills the half.
 */
function halfPoints(r: number): number[] {
  const cut = (r * (3 - Math.sqrt(3))) / 2;
  const s3 = (Math.sqrt(3) / 2) * r;
  return [cut, -cut, s3, -r / 2, s3, r / 2, 0, r, -cut, cut];
}

/**
 * The icon of one kind, cut out of the tile in the water's dark.
 *
 * `back` is the ink of the tile under the icon, and `backAlpha` its alpha:
 * the Forge Vent's inner flame and the Archive Vault's pages are the tile
 * showing through the cut, which is how the card draws them.
 */
function icon(
  g: MarkGraphics, kind: StructureKind, back: number, backAlpha: number,
): void {
  const ink = WATER, w = 3;
  switch (kind) {
    case 'spire': {
      // The relay arrow on its stem.
      g.poly([0, -13, 8, -3, 3, -3, 3, 12, -3, 12, -3, -3, -8, -3]);
      g.fill({ color: ink, alpha: 1 });
      break;
    }
    case 'forge': {
      // The flame, and the inner flame the tile shows through.
      const outer = [0, -14];
      cubic(outer, 0, -14, 9, -4, 9, 6, 0, 12);
      cubic(outer, 0, 12, -9, 6, -9, -4, 0, -14);
      g.poly(outer).fill({ color: ink, alpha: 1 });
      const inner = [0, -3];
      cubic(inner, 0, -3, 4, 2, 4, 6, 0, 8);
      cubic(inner, 0, 8, -4, 6, -4, 2, 0, -3);
      g.poly(inner).fill({ color: back, alpha: backAlpha });
      break;
    }
    case 'kelp': {
      // Three stalks, each with its head.
      line(g, -8, 13, -8, -6, ink, 1, w);
      line(g, 0, 13, 0, -13, ink, 1, w);
      line(g, 8, 13, 8, -3, ink, 1, w);
      disc(g, -8, -8, 2.2, ink, 1);
      disc(g, 0, -15, 2.2, ink, 1);
      disc(g, 8, -5, 2.2, ink, 1);
      break;
    }
    case 'battery': {
      // The torpedo: a round head on a body, with the fins behind it.
      g.moveTo(-8, -5).lineTo(8, -5)
        .arc(8, 0, 5, -Math.PI / 2, Math.PI / 2)
        .lineTo(-8, 5).closePath()
        .fill({ color: ink, alpha: 1 });
      g.poly([-8, -5, -14, -10, -14, 10, -8, 5]).fill({ color: ink, alpha: 1 });
      break;
    }
    case 'throne': {
      // The crown.
      g.poly([-14, 10, 14, 10, 14, -4, 7, 3, 0, -13, -7, 3, -14, -4]);
      g.fill({ color: ink, alpha: 1 });
      break;
    }
    case 'drydock': {
      // The cradle, with the hull in it.
      const pts = [-13, -10, -11, 5];
      quadratic(pts, -11, 5, 0, 15, 11, 5);
      pts.push(13, -10);
      g.poly(pts, false);
      stroke(g, ink, 1, w);
      const hull: number[] = [];
      ellipseArc(hull, 0, -1, 7, 3.5, 0, Math.PI * 2);
      g.poly(hull).fill({ color: ink, alpha: 1 });
      break;
    }
    case 'archive': {
      // The book, with the spine and the pages the tile shows through.
      g.poly([-11, -11, 11, -11, 11, 11, -11, 11]).fill({ color: ink, alpha: 1 });
      line(g, -5, -11, -5, 11, back, backAlpha, 2);
      line(g, 0, -5, 7, -5, back, backAlpha, 1.6);
      line(g, 0, 0, 7, 0, back, backAlpha, 1.6);
      line(g, 0, 5, 7, 5, back, backAlpha, 1.6);
      break;
    }
    case 'marshal': {
      // The balance: the post, the foot, the beam and the two pans.
      line(g, 0, -13, 0, 10, ink, 1, w);
      line(g, -8, 12, 8, 12, ink, 1, w);
      line(g, -13, -8, 13, -8, ink, 1, w);
      g.poly([-13, -8, -17, 2, -9, 2]).fill({ color: ink, alpha: 1 });
      g.poly([13, -8, 9, 2, 17, 2]).fill({ color: ink, alpha: 1 });
      break;
    }
    case 'ward': {
      // The mending cross.
      g.poly([
        -4, -14, 4, -14, 4, -4, 14, -4, 14, 4, 4, 4,
        4, 14, -4, 14, -4, 4, -14, 4, -14, -4, -4, -4,
      ]);
      g.fill({ color: ink, alpha: 1 });
      break;
    }
  }
}

/** One kind of the `tile` set, still. */
function markOf(kind: StructureKind): StructureDraw {
  const r = kind === 'throne' ? THRONE_TILE_R : TILE_R;
  const hex = hexPoints(r);
  const half = halfPoints(r);
  const seam = r * 0.64;
  return (g: MarkGraphics, ink: StructureInk): void => {
    const fillA = ink.held ? 0.78 : 0.34;
    g.poly(hex).fill({ color: ink.own, alpha: fillA });
    g.poly(hex);
    stroke(g, ink.own, 0.9, 1.5);
    if (ink.foe !== 0) {
      // The lower-right half, knocked out of the tile and taken by the
      // attacker. The water goes down first, because the holder's fill
      // stands under it.
      g.poly(half).fill({ color: WATER, alpha: 1 });
      g.poly(half).fill({ color: ink.foe, alpha: 0.8 });
      g.poly(half, false);
      stroke(g, ink.foe, 0.95, 1.5);
      line(g, seam, -seam, -seam, seam, WHITE, SEAM_ALPHA, 1.6);
    }
    icon(g, kind, ink.own, fillA + 0.1);
  };
}

/** Card 14, all nine kinds. */
export const TILE_SET: StructureSet = setOf(markOf);
